import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { Shirt, CheckCircle2, Clock, Eye } from "lucide-react";

const STATUS_META = {
  Confirmed:   { Icon: CheckCircle2, color: "#4ade80", label: "Confirmed", bg: "bg-green-500/15", border: "border-green-500/60" },
  Pending:     { Icon: Clock,        color: "#f5c518", label: "Pending",   bg: "bg-yellow-500/15", border: "border-yellow-500/60" },
  Speculation: { Icon: Eye,          color: "#4B92DB", label: "Speculation", bg: "bg-titans-blue/15", border: "border-titans-blue/60" },
};

export default function UniformTracker() {
  const [uniforms, setUniforms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/uniforms");
      setUniforms(data.uniforms || []);
    } catch (e) {
      console.error("UniformTracker load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const up = [];
    const pa = [];
    uniforms.forEach((u) => {
      const d = u.game_date ? new Date(u.game_date) : null;
      if (d && d < now) pa.push(u); else up.push(u);
    });
    up.sort((a, b) => (a.week || 0) - (b.week || 0));
    pa.sort((a, b) => (b.week || 0) - (a.week || 0));
    return { upcoming: up, past: pa };
  }, [uniforms]);

  if (loading) return <div className="text-titans-silver">Loading uniform tracker…</div>;

  const renderCard = (u, idx) => {
    const meta = STATUS_META[u.status] || STATUS_META.Pending;
    return (
      <div
        key={u.week}
        data-testid={`uniform-card-${u.week}`}
        className={`titans-card overflow-hidden border-l-4 ${meta.border}`}
      >
        {/* Header strip with swatch */}
        <div className="relative h-20 flex items-center" style={{ background: u.swatch || "#0C2340" }}>
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />
          <div className="relative px-5 flex items-center gap-3 w-full">
            <div className="bg-titans-red text-white font-display text-2xl px-3 py-1">
              W{u.week}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-cond uppercase tracking-[0.2em] text-[10px] text-white/80">
                {u.home_away || "—"} · vs {u.opponent || "TBD"}
              </div>
              <div className="font-display text-xl text-white uppercase truncate">
                {u.preset_label || "TBD"}
              </div>
            </div>
            <div className={`inline-flex items-center gap-1 ${meta.bg} border ${meta.border} px-2 py-1`}>
              <meta.Icon size={12} style={{ color: meta.color }} />
              <span className="font-cond uppercase tracking-wider text-[10px]" style={{ color: meta.color }}>
                {meta.label}
              </span>
            </div>
          </div>
        </div>

        {/* Body: jersey/pants/helmet + date + note */}
        <div className="p-5">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Jersey</div>
              <div className="font-display text-base mt-0.5 text-white">{u.jersey || "—"}</div>
            </div>
            <div>
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Pants</div>
              <div className="font-display text-base mt-0.5 text-white">{u.pants || "—"}</div>
            </div>
            <div>
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Helmet</div>
              <div className="font-display text-base mt-0.5 text-white">{u.helmet || "—"}</div>
            </div>
          </div>
          {u.photo && (
            <img src={u.photo} alt={`Week ${u.week} uniform`} className="w-full h-auto mb-3 border border-titans-elev" loading="lazy" />
          )}
          <div className="flex items-center justify-between flex-wrap gap-2">
            {u.game_date && (
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-blue">
                {formatDate(u.game_date, { weekday: "short", month: "short", day: "numeric" })}
              </div>
            )}
            {u.note && <div className="text-titans-silver text-xs font-body italic flex-1 text-right">{u.note}</div>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="uniform-tracker">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue">Sunday Threads</div>
          <h2 className="font-display text-3xl md:text-4xl uppercase tracking-wide section-title flex items-center gap-2">
            <Shirt size={24} className="text-titans-red" /> Uniform Tracker
          </h2>
          <p className="text-titans-silver text-sm font-body max-w-xl mt-2">
            What the Titans will wear each Sunday — confirmed combos, plus our editorial guesses for reveal-pending weeks.
          </p>
        </div>
      </div>

      {upcoming.length === 0 && past.length === 0 ? (
        <div className="titans-card p-10 text-center">
          <Shirt size={42} className="mx-auto text-titans-blue mb-3" />
          <div className="font-display text-2xl uppercase">No Combos Posted Yet</div>
          <div className="text-titans-silver font-body mt-1">
            The owner will lock in the weekly threads in the admin console.
          </div>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="mb-10">
              <h3 className="font-cond uppercase tracking-[0.25em] text-[11px] text-titans-silver mb-3">
                Upcoming · {upcoming.length}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {upcoming.map(renderCard)}
              </div>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <h3 className="font-cond uppercase tracking-[0.25em] text-[11px] text-titans-silver mb-3">
                Past Combos · {past.length}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 opacity-90">
                {past.map(renderCard)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
